import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Controller, useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CheckCircle2,
  FileText,
  Landmark,
  Loader2,
  Paperclip,
  ShieldCheck,
  X,
} from 'lucide-react'
import { SITE } from '../../data/site'
import type { Vehicle, VehicleColour } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import { useCreateBooking } from '../../hooks/useVehicleQueries'
import { useCities } from '../../hooks/useCities'
import { useToast } from '../ui/toast-context'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { useGsapContext } from '../../hooks/useGsapReveal'
import { revealDialog } from '../../animations/gsapAnimations'
import { toApiError } from '../../lib/http'
import { fileSize, pkr } from '../../utils/format'
import Button from '../ui/Button'
import Combobox from '../ui/Combobox'

/* Booking a car: who you are, where it goes, and proof the token payment
   has been made.

   It posts to the API rather than opening WhatsApp — a booking has to land
   in the console the moment it is made, which a message somebody has to read
   and re-type does not do. The receipt is the whole reason this is a form
   and not a chat: it is a file, it is checked by the desk, and it belongs
   filed against the record rather than in a thread. */

/* Pakistani mobile numbers, however people actually write them: 03001234567,
   +92 300 1234567, 0300-1234567. Same rule the API enforces — the two are
   deliberately identical so nothing passes here and fails there. */
const PHONE = /^(\+92|0)?[\s-]?3\d{2}[\s-]?\d{7}$/

const MAX_RECEIPT = 8 * 1024 * 1024
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf']

const schema = z.object({
  fullName: z.string().trim().min(3, 'Please give your full name'),
  city: z.string().trim().min(2, 'Which city are you in?'),
  address: z.string().trim().min(6, 'Please give a complete address'),
  gender: z.enum(['Male', 'Female'], { message: 'Please choose an option' }),
  contact: z.string().trim().regex(PHONE, 'Enter a valid mobile number'),
  email: z
    .string()
    .trim()
    .min(1, 'We need an email address')
    .email('That email does not look right'),
})

type BookingForm = z.infer<typeof schema>

const BANK = {
  title: 'Sultan Motors 28',
  bank: 'Meezan Bank',
  iban: 'PK36 MEZN 0001 2803 5865 3861',
  account: '0128-0358653861',
}

type BookingModalProps = {
  vehicle: Vehicle
  /** The paint the visitor picked on the swatches before pressing Book. The
      car's own when they left them alone, and null on a car nobody has
      recorded a colour on. */
  colour?: VehicleColour | null
  open: boolean
  onClose: () => void
}

function BookingModal({ vehicle, colour = null, open, onClose }: BookingModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, open)

  const { toast } = useToast()
  const booking = useCreateBooking()

  /* Only asked for once the dialog is open — a visitor scrolling the listing
     has no use for the city list. */
  const cities = useCities(open)

  const [receipt, setReceipt] = useState<File | null>(null)
  const [receiptError, setReceiptError] = useState<string | null>(null)
  /* The reference the customer is told to quote. Its presence is what turns
     the dialog from a form into a confirmation. */
  const [reference, setReference] = useState<string | null>(null)

  /* Enter-only, for the reason page transitions are: an exit tween holds a
     dismissed dialog on screen after the user has closed it. Keyed on `open`
     because the panel is unmounted while closed. */
  const dialogRef = useGsapContext<HTMLDivElement>(
    (scope) => {
      const panel = scope.querySelector('[data-dialog="panel"]')
      if (panel) revealDialog(panel, scope.querySelector('[data-dialog="backdrop"]'))
    },
    [open],
  )

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<BookingForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      city: SITE.city,
      address: '',
      gender: 'Male',
      contact: '',
      email: '',
    },
  })

  /* The file lives outside react-hook-form: a FileList is not a value the
     resolver can usefully validate, and the two checks that matter — type
     and size — read better spelled out than expressed as a schema. */
  const pickReceipt = (file: File | null) => {
    setReceipt(file)

    if (!file) return setReceiptError(null)
    if (!ACCEPTED.includes(file.type)) {
      return setReceiptError('Attach a JPG, PNG, WebP or PDF.')
    }
    if (file.size > MAX_RECEIPT) {
      return setReceiptError('That file is over 8 MB. Try a smaller photo.')
    }
    setReceiptError(null)
  }

  const close = () => {
    onClose()
    /* Cleared on the way out, not the way in: a dialog reset at open flashes
       the previous booking's fields for a frame before emptying them. */
    reset()
    setReceipt(null)
    setReceiptError(null)
    setReference(null)
    booking.reset()
  }

  /* The paint being booked: the swatch the visitor pressed, or — on a car
     they never touched one on — the colour it is standing in. */
  const paint = colour?.name || vehicle.colour || ''

  const onSubmit: SubmitHandler<BookingForm> = async (data) => {
    if (!receipt) return setReceiptError('Attach the transaction receipt.')
    if (receiptError) return

    try {
      /* The paint goes with the order. It is the one thing on this form the
         visitor chose before opening it, and a booking that does not carry
         it leaves the desk to guess or to ask again. */
      const created = await booking.mutateAsync({
        ...data,
        variantId: vehicle.variantId,
        year: vehicle.year,
        colour: paint,
        receipt,
      })

      setReference(created.reference)
      toast({
        tone: 'success',
        title: `Booking ${created.reference} received`,
        body: `We are checking your receipt for the ${nameOf(vehicle)}. Someone will call ${created.contact} shortly.`,
      })
    } catch (error) {
      const failure = toApiError(error)

      /* A field-level rejection from the API is put back on the field that
         caused it, so the fix is where the mistake is rather than in a
         banner at the top of a form the visitor has to re-read. */
      if (failure.fields) {
        for (const [field, message] of Object.entries(failure.fields)) {
          if (field === 'receipt') setReceiptError(message)
          else if (field in schema.shape) {
            setError(field as keyof BookingForm, { message })
          }
        }
      }

      toast({ tone: 'error', title: 'That booking did not go through', body: failure.message })
    }
  }

  if (!open) return null

  const field =
    'mt-2 w-full rounded-xl border border-line bg-raised px-4 py-3 text-[15px] text-platinum placeholder:text-dim focus:border-lime focus:outline-none'
  const label =
    'label-sm text-steel'
  const message = 'mt-2 text-[12.5px] text-lime-deep'

  /* Rendered into <body>, not in place.

     A card sits inside a list item the reveal animation has given a
     transform, and a transformed ancestor becomes the containing block for
     anything position: fixed underneath it — which pinned this dialog to the
     inside of the card that opened it, backdrop and all. A portal takes the
     question off the table, the same way the console's drawer does. */
  return createPortal(
    <div
      ref={dialogRef}
      className="fixed inset-0 z-60 flex items-end justify-center sm:items-center"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={close}
        data-dialog="backdrop"
        className="absolute inset-0 bg-void/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        data-dialog="panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-title"
        className="relative max-h-[92svh] w-full max-w-xl overflow-y-auto rounded-t-3xl border border-line bg-elevated p-6 shadow-pop sm:rounded-2xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <p className="label-sm text-lime-deep">
              {reference ? 'Booking received' : 'Book this car'}
            </p>
            <h2
              id="booking-title"
              className="mt-3 font-display text-2xl font-semibold tracking-[-0.03em] text-platinum"
            >
              {nameOf(vehicle)}
            </h2>
            <p className="mt-1 text-[13.5px] text-steel">
              {vehicle.year > 0 ? vehicle.year : 'Model year on request'}
            </p>
          </div>

          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="grid size-10 shrink-0 place-items-center rounded-lg border border-line text-silver transition hover:border-lime"
          >
            <X aria-hidden="true" strokeWidth={1.8} className="size-4.5" />
          </button>
        </div>

        {reference ? (
          <Confirmation reference={reference} vehicle={vehicle} onClose={close} />
        ) : (
          <>
            <SelectedColour name={paint} hex={colour?.hex ?? ''} labelClass={label} />

            <PaymentPanel
              price={vehicle.price}
              bookingDownPayment={vehicle.bookingDownPayment}
            />

            {vehicle.bookingDownPayment > 0 ? (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-6 flex flex-col gap-5"
              noValidate
            >
              <div>
                <label htmlFor="bk-name" className={label}>
                  Full name
                </label>
                <input
                  id="bk-name"
                  autoComplete="name"
                  {...register('fullName')}
                  className={field}
                  placeholder="As it appears on your CNIC"
                />
                {errors.fullName ? (
                  <p role="alert" className={message}>
                    {errors.fullName.message}
                  </p>
                ) : null}
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="bk-city" className={label}>
                    City
                  </label>
                  {/* Seeded rather than free text, so the city column is
                      still groupable a month from now — but typing is the
                      way in, because scrolling thirty rows on a phone is
                      worse than typing three letters. */}
                  <Controller
                    name="city"
                    control={control}
                    render={({ field: city }) => (
                      <Combobox
                        id="bk-city"
                        value={city.value}
                        onChange={city.onChange}
                        loading={cities.isPending}
                        placeholder="Choose your city"
                        searchPlaceholder="Type to search…"
                        aria-invalid={Boolean(errors.city)}
                        options={(cities.data ?? []).map((entry) => ({
                          value: entry.name,
                          label: entry.name,
                          hint: entry.region,
                          keywords: entry.keywords,
                        }))}
                      />
                    )}
                  />
                  {errors.city ? (
                    <p role="alert" className={message}>
                      {errors.city.message}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="bk-contact" className={label}>
                    Contact number
                  </label>
                  <input
                    id="bk-contact"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    {...register('contact')}
                    className={field}
                    placeholder="0300 1234567"
                  />
                  {errors.contact ? (
                    <p role="alert" className={message}>
                      {errors.contact.message}
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Asked for alongside the number, not instead of it: the
                  desk calls, but the booking confirmation, the invoice and
                  the delivery paperwork are all things that have to be sent
                  somewhere the customer keeps. */}
              <div>
                <label htmlFor="bk-email" className={label}>
                  Email
                </label>
                <input
                  id="bk-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  {...register('email')}
                  className={field}
                  placeholder="you@example.com"
                />
                {errors.email ? (
                  <p role="alert" className={message}>
                    {errors.email.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="bk-address" className={label}>
                  Address
                </label>
                <textarea
                  id="bk-address"
                  rows={2}
                  autoComplete="street-address"
                  {...register('address')}
                  className={`${field} resize-none`}
                  placeholder="House, street, sector"
                />
                {errors.address ? (
                  <p role="alert" className={message}>
                    {errors.address.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label htmlFor="bk-gender" className={label}>
                  Gender
                </label>
                <select id="bk-gender" {...register('gender')} className={field}>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {errors.gender ? (
                  <p role="alert" className={message}>
                    {errors.gender.message}
                  </p>
                ) : null}
              </div>

              <ReceiptField
                file={receipt}
                error={receiptError}
                onPick={pickReceipt}
                labelClass={label}
              />

              <Button
                type="submit"
                size="lg"
                disabled={booking.isPending}
                className="w-full"
              >
                {booking.isPending ? (
                  <>
                    <Loader2
                      aria-hidden="true"
                      className="size-4 animate-spin"
                      strokeWidth={2.2}
                    />
                    Filing your booking
                  </>
                ) : (
                  'Confirm booking'
                )}
              </Button>

              <p className="text-[12px] leading-relaxed text-dim">
                Your details go straight to the showroom desk. We check the
                receipt against our account before the car is held — nothing is
                charged from this page.
              </p>
            </form>
            ) : (
              <p className="mt-5 rounded-xl border border-line bg-raised px-4 py-3 text-[13px] leading-relaxed text-silver">
                Online receipt submission is unavailable until the showroom publishes this
                variant-year's booking amount. Call{' '}
                <a className="font-semibold text-lime hover:underline" href={SITE.phoneHref}>
                  {SITE.phone}
                </a>{' '}
                to confirm it first.
              </p>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* ---------- the payment brief ---------- */

/* Where the money goes, before anything is asked for. A form that requests
   a receipt without first saying who to pay is a form nobody can complete. */
function PaymentPanel({
  price,
  bookingDownPayment,
}: {
  price: number
  bookingDownPayment: number
}) {
  if (bookingDownPayment <= 0) {
    return (
      <section
        aria-labelledby="payment-title"
        className="mt-6 rounded-xl border border-line bg-raised px-5 py-5"
      >
        <h3 id="payment-title" className="flex items-center gap-2 label-sm text-platinum">
          <Landmark aria-hidden="true" strokeWidth={1.8} className="size-3.5 text-lime" />
          Booking amount on request
        </h3>
        <p className="mt-3 text-[13.5px] leading-relaxed text-silver">
          This variant-year does not have a booking payment stated yet. Confirm the amount with
          the showroom before transferring money or attaching a receipt.
        </p>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="payment-title"
      className="mt-6 rounded-xl border border-lime/40 bg-lime-ink px-5 py-5"
    >
      <h3
        id="payment-title"
        className="flex items-center gap-2 label-sm text-lime-deep"
      >
        <Landmark aria-hidden="true" strokeWidth={1.8} className="size-3.5" />
        Step one · transfer the token
      </h3>

      <p className="mt-3 text-[13.5px] leading-relaxed text-silver">
        Transfer{' '}
        <strong className="font-semibold text-platinum">
          PKR {pkr.format(bookingDownPayment)}
        </strong>{' '}
        to place your booking, then attach the receipt below. The payment is adjusted
        against {price > 0 ? `the PKR ${pkr.format(price)} price` : 'the agreed price'} — or refunded in full if the
        booking does not go ahead.
      </p>

      <dl className="mt-4 grid gap-2 text-[12.5px]">
        {(
          [
            ['Title', BANK.title],
            ['Bank', BANK.bank],
            ['IBAN', BANK.iban],
            ['Account', BANK.account],
          ] as const
        ).map(([term, value]) => (
          <div key={term} className="flex items-baseline justify-between gap-4">
            <dt className="shrink-0 text-dim">{term}</dt>
            <dd className="text-right text-silver">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/* ---------- the colour that was picked ---------- */

/* The paint, read back rather than asked for again.

   The colour was chosen on the swatches before this dialog opened, and it is
   filed with the order — so what belongs here is a confirmation of the pick,
   not a second control that could disagree with the first. The free-text box
   that used to invite "a colour you want" is gone for the same reason:
   asking twice is how a booking ends up naming a paint nobody offered.

   Nothing is rendered for a car nobody has recorded a colour on: an empty
   swatch beside an empty name says less than the absence of the row. */
function SelectedColour({
  name,
  hex,
  labelClass,
}: {
  name: string
  hex: string
  labelClass: string
}) {
  if (!name) return null

  return (
    <div className="mt-6">
      <p className={labelClass}>Selected colour</p>

      <div className="mt-2 flex items-center gap-3 rounded-xl border border-line bg-raised px-4 py-3">
        {hex ? (
          <span
            aria-hidden="true"
            className="size-7 shrink-0 rounded-full border border-line-soft"
            style={{ backgroundColor: hex }}
          />
        ) : null}

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] text-platinum">{name}</span>
          <span className="mt-0.5 block text-[12px] text-dim">The colour you picked</span>
        </span>
      </div>

      {/* Said once, so nobody wonders whether the swatch they pressed
          registered. */}
      <p className="mt-2 text-[12px] leading-relaxed text-dim">
        This is your requested colour. We will confirm the actual car and its
        colour with you. To change your request, close this and pick another swatch.
      </p>
    </div>
  )
}

/* ---------- the receipt ---------- */

function ReceiptField({
  file,
  error,
  onPick,
  labelClass,
}: {
  file: File | null
  error: string | null
  onPick: (file: File | null) => void
  labelClass: string
}) {
  return (
    <div>
      <label htmlFor="bk-receipt" className={labelClass}>
        Transaction receipt
      </label>

      {/* The native input is the control — it is hidden visually rather than
          replaced, so the label, keyboard focus and the file picker all
          behave the way the platform's do. */}
      <input
        id="bk-receipt"
        type="file"
        accept={ACCEPTED.join(',')}
        onChange={(event) => onPick(event.target.files?.[0] ?? null)}
        className="peer sr-only"
      />

      <label
        htmlFor="bk-receipt"
        className="mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-line bg-raised px-4 py-3.5 transition hover:border-lime peer-focus-visible:border-lime"
      >
        {file ? (
          <FileText aria-hidden="true" strokeWidth={1.7} className="size-5 shrink-0 text-mint-deep" />
        ) : (
          <Paperclip aria-hidden="true" strokeWidth={1.7} className="size-5 shrink-0 text-lime-deep" />
        )}

        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] text-platinum">
            {file ? file.name : 'Attach the payment slip'}
          </span>
          <span className="mt-0.5 block text-[12px] text-dim">
            {file
              ? `${fileSize(file.size)} · tap to replace`
              : 'Screenshot or PDF from your bank app · up to 8 MB'}
          </span>
        </span>
      </label>

      {error ? (
        <p role="alert" className="mt-2 text-[12.5px] text-lime-deep">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/* ---------- what happens next ---------- */

function Confirmation({
  reference,
  vehicle,
  onClose,
}: {
  reference: string
  vehicle: Vehicle
  onClose: () => void
}) {
  return (
    <div className="mt-7">
      <div className="flex items-start gap-3 rounded-2xl border border-mint/30 bg-mint/8 px-5 py-4">
        <CheckCircle2
          aria-hidden="true"
          strokeWidth={1.8}
          className="mt-0.5 size-5 shrink-0 text-mint-deep"
        />
        <div>
          <p className="text-[14.5px] font-semibold text-platinum">
            Your booking is with the desk
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-steel">
            Quote reference{' '}
            <strong className="font-semibold text-platinum">{reference}</strong>{' '}
            when you call. We will verify the transfer and confirm which car
            will be allocated to your {nameOf(vehicle)} order.
          </p>
        </div>
      </div>

      <ol className="mt-6 space-y-3.5">
        {[
          'We check the receipt against our account — usually within the hour.',
          'A salesperson calls you to confirm the unit, colour and delivery date.',
          'On the day, the paperwork is cleared and the car is handed over.',
        ].map((step, index) => (
          <li key={step} className="flex gap-3 text-[13.5px] leading-relaxed text-silver">
            <span className="grid size-6 shrink-0 place-items-center rounded-lg border border-line bg-raised text-[11px] text-lime-deep">
              {index + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>

      <p className="mt-6 flex items-start gap-2 text-[12px] leading-relaxed text-dim">
        <ShieldCheck aria-hidden="true" strokeWidth={1.7} className="mt-0.5 size-4 shrink-0" />
        If anything is wrong with the transfer, the token is returned in full.
        Call {SITE.phone} and quote the reference.
      </p>

      <Button size="lg" className="mt-7 w-full" onClick={onClose}>
        Done
      </Button>
    </div>
  )
}

export default BookingModal
